import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { KeyRound, LockKeyhole } from "lucide-react";
import { AuthField, AuthSplitLayout } from "@/components/AuthSplitLayout";
import { api, formatApiError } from "@/lib/api";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const tokenFromLink = params.get("token") || "";
  const [token, setToken] = useState(tokenFromLink);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  async function submit(event) {
    event.preventDefault();
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setError("");
    setIsSaving(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <AuthSplitLayout
      testId="reset-password-page"
      statement="Choose a new password and get back to work."
      support="Reset links come from your TAGForce email and expire one hour after they were requested."
      heading={done ? "Password updated" : "Set a new password"}
      description={done ? "Your password has been changed. Sign in with your new password to continue." : "Pick a password only you know. At least 8 characters."}
      footer={
        !done && (
          <Link className="login-link-button login-return" data-testid="reset-password-back-link" to="/login">
            Back to sign in
          </Link>
        )
      }
    >
      {done ? (
        <div className="login-form">
          <p className="form-message success-message" data-testid="reset-password-success">
            Your password has been changed.
          </p>
          <Link className="login-submit login-submit-link" data-testid="reset-password-login-link" to="/login">
            Go to sign in
          </Link>
        </div>
      ) : (
        <form className="login-form" onSubmit={submit}>
          {!tokenFromLink && (
            <AuthField id="reset-token" label="Reset token" icon={KeyRound}>
              <input
                data-testid="reset-password-token-input"
                id="reset-token"
                placeholder="Paste the token from your email"
                value={token}
                onChange={(event) => setToken(event.target.value)}
                required
              />
            </AuthField>
          )}
          <AuthField id="new-password" label="New password" icon={LockKeyhole}>
            <input
              data-testid="reset-password-new-input"
              id="new-password"
              type="password"
              placeholder="At least 8 characters"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              minLength="8"
              autoComplete="new-password"
            />
          </AuthField>
          <AuthField id="confirm-password" label="Confirm new password" icon={LockKeyhole}>
            <input
              data-testid="reset-password-confirm-input"
              id="confirm-password"
              type="password"
              placeholder="Repeat your new password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              required
              minLength="8"
              autoComplete="new-password"
            />
          </AuthField>
          {error && (
            <p className="form-message error-message" data-testid="reset-password-error">{error}</p>
          )}
          <button className="login-submit" data-testid="reset-password-submit-button" disabled={isSaving} type="submit">
            {isSaving ? "Saving…" : "Save new password"}
          </button>
        </form>
      )}
    </AuthSplitLayout>
  );
}
